import { useState } from 'react'
import { formatDate } from '@shared/utils/datetime'
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  Stack,
  Card,
  CardContent,
  Divider,
  useTheme,
  alpha
} from '@mui/material'
import {
  AccessTime,
  Upgrade,
  CheckCircle,
  Lock
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useTrialExpiration } from '@shared/contexts/TrialExpirationContext'

export const TrialExpiredModal = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const { trialExpired, clearTrialExpired } = useTrialExpiration()
  const [isLoading, setIsLoading] = useState(false)

  const handleContinueToBilling = async () => {
    setIsLoading(true)
    try {
      // Navigate to admin billing page where payment can be added
      clearTrialExpired()
      navigate('/admin/billing')
    } catch (error) {
      console.error('Failed to navigate to billing:', error)
    } finally {
      setIsLoading(false)
    }
  }

  if (!trialExpired.isExpired) {
    return null
  }

  const trialEndDate = trialExpired.trialEndedAt
    ? formatDate(trialExpired.trialEndedAt, {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    : 'recently'

  return (
    <Dialog
      open={trialExpired.isExpired}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden'
        }
      }}
      // Prevent closing by clicking outside or pressing escape
      disableEscapeKeyDown
      onClose={() => {}}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: 'white',
            py: 4,
            px: 3,
            textAlign: 'center'
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              backgroundColor: alpha(theme.palette.common.white, 0.2),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
              border: `3px solid ${alpha(theme.palette.common.white, 0.3)}`
            }}
          >
            <Lock sx={{ fontSize: 40 }} />
          </Box>
          <Typography variant="h4" fontWeight="700" gutterBottom>
            Your Trial Has Ended
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.95, maxWidth: 500, margin: '0 auto' }}>
            Your trial period ended on {trialEndDate}. To continue using the helpdesk system,
            please add a payment method to activate your account.
          </Typography>
        </Box>

        <Box sx={{ p: 4 }}>
          <Stack spacing={3}>
            {/* What you get section */}
            <Box>
              <Typography variant="h6" fontWeight="600" gutterBottom color="text.primary">
                Continue Where You Left Off
              </Typography>
              <Stack spacing={1.5} sx={{ mt: 2 }}>
                {[
                  'Unlimited ticket management',
                  'Multi-organization support',
                  'Advanced analytics and reporting',
                  'Email notifications and automation',
                  'Customer support',
                  'Custom branding and workflows'
                ].map((feature, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <CheckCircle sx={{ color: theme.palette.success.main, fontSize: 20 }} />
                    <Typography variant="body2" color="text.secondary">
                      {feature}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>

            <Divider />

            {/* Action button */}
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={handleContinueToBilling}
              disabled={isLoading}
              startIcon={<Upgrade />}
              sx={{
                py: 1.5,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                '&:hover': {
                  boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
                  transform: 'translateY(-2px)',
                  transition: 'all 0.2s ease-in-out'
                }
              }}
            >
              Add Payment Method to Continue
            </Button>

            {/* Help text */}
            <Card
              sx={{
                backgroundColor: alpha(theme.palette.info.main, 0.05),
                border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`
              }}
            >
              <CardContent sx={{ py: 2 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <AccessTime sx={{ color: theme.palette.info.main, mt: 0.5 }} />
                  <Box>
                    <Typography variant="body2" fontWeight="600" color="text.primary" gutterBottom>
                      Need Help?
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Contact our sales team at{' '}
                      <Box
                        component="a"
                        href="mailto:sales@helpdesk.com"
                        sx={{
                          color: theme.palette.primary.main,
                          textDecoration: 'none',
                          fontWeight: 600,
                          '&:hover': { textDecoration: 'underline' }
                        }}
                      >
                        sales@helpdesk.com
                      </Box>{' '}
                      or call us at{' '}
                      <Box
                        component="span"
                        sx={{ color: theme.palette.primary.main, fontWeight: 600 }}
                      >
                        1-800-HELPDESK
                      </Box>
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
